import { createAction, createReducer } from "@reduxjs/toolkit"
import httpClient from "../../../api/httpClient"
import { actions as notifier } from "../../../component/notifier"
import licenseMessages from "./licenseMessages"

const LOCAL_STORAGE_LICENSE_ACCEPTANCE_KEY = "licenseAccepted"

const initialState = {
  dialogIsShowing: false,
  dialogIsPreview: false
}

const SET_LICENSE_CONFIGURATION = createAction("SET_LICENSE_CONFIGURATION")
const SET_LICENSE_DIALOG_IS_SHOWING = createAction(
  "SET_LICENSE_DIALOG_IS_SHOWING"
)

function fetchLicenseConfiguration() {
  return async (dispatch) => {
    try {
      const { data } = await httpClient.get("/api/license-agreement")
      dispatch(SET_LICENSE_CONFIGURATION(data))
      return data
    } catch (error) {
      const message = error.response?.data?.message ?? error.message
      dispatch(notifier.enqueueError(message))
      return undefined
    }
  }
}

function saveLicenseConfiguration(config) {
  return (dispatch) =>
    httpClient
      .post("/api/license-agreement", config)
      .then(() => {
        const message = config.enabled
          ? licenseMessages.licenseSaveSuccess
          : licenseMessages.licenseDisableSuccess

        dispatch(notifier.enqueueSuccess(message))
      })
      .catch((error) => {
        const message = error.response?.data?.message ?? error.message
        dispatch(notifier.enqueueError(message))
      })
}

function acceptLicense() {
  return async (dispatch) => {
    dispatch(SET_LICENSE_DIALOG_IS_SHOWING(false))
    localStorage.setItem(LOCAL_STORAGE_LICENSE_ACCEPTANCE_KEY, true)
  }
}

function rejectLicense() {
  return async (dispatch) => {
    dispatch(SET_LICENSE_DIALOG_IS_SHOWING(false))
    localStorage.removeItem(LOCAL_STORAGE_LICENSE_ACCEPTANCE_KEY)
  }
}

/**
 * Verifies whether the license agreement dialog should be shown, and shows it if it should.
 * NOTE: To be called only when the license configuration is already fetched.
 */
function checkLicenseAgreement() {
  return async (dispatch, getState) => {
    const { configuration } = getState().license
    const enabled = configuration?.enabled ?? false
    if (!enabled) {
      return
    }

    const acceptanceState = localStorage.getItem(
      LOCAL_STORAGE_LICENSE_ACCEPTANCE_KEY
    )

    const licenseAccepted = JSON.parse(acceptanceState) ?? false

    if (!licenseAccepted) {
      dispatch(
        SET_LICENSE_DIALOG_IS_SHOWING({ isShowing: true, isPreview: false })
      )
    }
  }
}

function setLicenseDialogIsShowing({ isShowing, isPreview } = {}) {
  return (dispatch) => {
    dispatch(SET_LICENSE_DIALOG_IS_SHOWING({ isShowing, isPreview }))
  }
}

const reducer = createReducer(initialState, {
  SET_LICENSE_CONFIGURATION(state, { payload }) {
    return {
      ...state,
      configuration: payload
    }
  },
  SET_LICENSE_DIALOG_IS_SHOWING(state, { payload }) {
    const { isShowing, isPreview } = payload
    const newState = { ...state, dialogIsShowing: isShowing }

    // Prevents jittery exit animation by not changing this property on exit
    if (isPreview != null) {
      newState.dialogIsPreview = isPreview
    }

    return newState
  }
})

export default {
  initialState,
  reducer,
  actions: {
    saveLicenseConfiguration,
    fetchLicenseConfiguration,
    checkLicenseAgreement,
    setLicenseDialogIsShowing,
    acceptLicense,
    rejectLicense
  }
}
